import React, {
  KeyboardEvent as ReactKeyboardEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import AutoCompletedInput, {
  Props as AutoCompleteProps,
} from 'core/components/AutoComplete';
import Spinner from 'pages/Dashboard/components/Spinner';
import isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  PagePropertiesDTO,
} from 'dtos/pagePropertiesDTO';
import {
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import DropdownOptionRow from 'core/components/Lookup/DropdownOptionRow';
import Dropdown from 'core/components/Lookup/Dropdown';
import clsx from 'clsx';
import omit from 'lodash/omit';

type PaginatedData<TOption> = {
  data?: TOption[] | null;
  pageProperties?: PagePropertiesDTO;
};

type OptionsDataResult<TOption> = Omit<UseQueryResult<PaginatedData<TOption>, Error>, 'data'> & {
  data?: PaginatedData<TOption> | TOption[];
};

type Parts = 'dropdownContainer' | 'listbox';

type Props<TOption> = Omit<AutoCompleteProps<TOption>, 'getLabel' | 'options'> & {
  value?: TOption | TOption[] | null;
  onChange: (value: TOption | TOption[]) => void;

  /**
   * Options are only used when the options are
  not fetched from the server by the lookup component */
  options?: TOption[];
  isLoading?: boolean;

  /**
   * Open the dropdown on load
   */
  openOnLoad?: boolean;

  /**
   * Custom filter based on the search when options are not fetched from the server
   */
  customFilterOptions?: (options: TOption[], searchValue: string) => TOption[];

  /**
   * Options are fetched from the server
   */
  getOptions?: (...props: any[]) => Promise<PaginatedData<TOption>>;
  defaultPageSize?: number;

  /**
   * Needed to create unique id for the options,
   to determine if the search value is new and needed to show + Add New */
  getUniqueId: (value: TOption) => string;

  /**
   * Custom row in the dropdown
   */
  renderOptionRow?: (option: TOption) => ReactNode;

  /**
   * when renderOptionRow is not provided, this is used to display the option in the dropdown
   */
  getLabel: (value: TOption) => string;

  /**
   * Used to create a new option,
   * should save the value in the server or/and return the normalize options from search text
   * return null if the option is not created instantly
   */
  onAddNew?: (value: string) => Promise<TOption | null>;

  /**
   * Unique id for the lookup
   */
  id?: string;

  /*
  * Custom type to show on + add new
  */
  type?: string;

  /**
   * Minimum search size to start fetching options from the server
   * @default 1
   */
  minSearchSize?: number;

  /**
   * React Query cache key prefix for server-fetched options.
   * Use a unique value per lookup to avoid cache collisions.
   * @default 'options'
   */
  dataKey?: string;

  /**
   * Extra params merged into the server fetch call and react-query cache key.
   */
  queryOptions?: Record<string, unknown>;

  /**
   * Message shown in the dropdown when a search completes with no results.
   * @default 'No options found'
   */
  emptyText?: string;

  classes?: Partial<Record<Parts, string>>;
};

type KeyboardEvent = ReactKeyboardEvent<HTMLDivElement> & {
  defaultMuiPrevented?: boolean;
};

function useOptionsData<T>({
  data = [],
  disabled,
  dataKey,
  queryParams,
  queryOptions,
  getData,
}: {
  data?: T[];
  disabled?: boolean;
  dataKey?: string;
  queryParams?: Record<string, any>;
  queryOptions?: Record<string, unknown>;
  getData?: (...props: any[]) => Promise<PaginatedData<T>>;
}): OptionsDataResult<T> {
  const queryResult = useQuery<PaginatedData<T>, Error>({
    queryKey: [dataKey ?? '', queryParams?.search, queryOptions],
    queryFn: () => (
      !isNil(getData)
        ? getData(queryParams)
        : Promise.resolve({ data })
    ),
    enabled: !disabled && !isNil(getData),
  });

  return {
    ...queryResult,
    data: !isNil(getData) ? queryResult.data : data,
  };
}

export default function PopulateLookup<TOption>({
  readonly = false,
  value,
  onChange,
  options: defaultOptions,
  isLoading: defaultIsLoading = false,
  getOptions,
  defaultPageSize = 10,
  getUniqueId,
  onAddNew,
  isOptionEqualToValue,
  id,
  getLabel,
  multiple = false,
  renderTags = () => null,
  disableRemove = true,
  renderOptionRow,
  customFilterOptions,
  minSearchSize = 1,
  dataKey = 'options',
  queryOptions = {},
  type,
  emptyText,
  classes,
  clearOnBlur = false,
  blurOnSelect = false,
  disableCloseOnSelect = false,
  openOnLoad = true,
  inputRef,
  InputProps,
  ...props
}: Props<TOption>) {
  const [searchValue, setSearchValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageChanged, setPageChanged] = useState<boolean>(false);

  const searchEnabled = searchValue?.length >= minSearchSize;

  const fetchNextPage = useCallback(() => {
    setPageChanged(true);
    setPage(page + 1);
  }, [page, setPage]);

  const handleInputChange = useCallback(debounce((
    event: SyntheticEvent,
    newInputValue: string,
    reason: string,
  ) => {
    if (open && (reason === 'input' || (reason === 'blur' && clearOnBlur))) {
      setSearchValue(newInputValue);
    }
  }, 700), [open]);

  const {
    data,
    isFetching,
    isFetched,
    isLoading: _isLoading,
    refetch,
  } = useOptionsData<TOption>({
    data: defaultOptions,
    disabled: readonly || !searchEnabled,
    dataKey,
    queryOptions,
    queryParams: {
      search: searchValue.trim(),
      searchText: searchValue.trim(),
      page: 0,
      pageSize: (page + 1) * defaultPageSize,
      ...queryOptions,
    },
    getData: getOptions,
  });

  useEffect(() => {
    if (page > 0) {
      refetch();
    }
  }, [page]);

  const isLoadingMore = useMemo(() => (
    isFetching && pageChanged
  ), [isFetching, pageChanged]);

  const {
    data: options = [],
    pageProperties = undefined,
  } = ((data as PaginatedData<TOption>)?.pageProperties
    ? (data as PaginatedData<TOption>)
    : { data }) ?? {};

  const isLoading = useMemo(() => (
    _isLoading || defaultIsLoading
  ), [_isLoading, defaultIsLoading]);

  const isServerLookup = !isNil(getOptions);
  const awaitingInitialSearch = isServerLookup && !isFetched && !isFetching;

  useEffect(() => {
    if ((options as TOption[])?.length > 0 && !isLoading && openOnLoad) {
      setTimeout(() => {
        setOpen(true);
      }, 100);
    }
  }, [options]);

  const optionSet = new Set((options as TOption[])?.map(
    (option) => getUniqueId?.(option).toLowerCase(),
  ));

  const handleChange = (value: TOption | TOption[]) => {
    if (!disableCloseOnSelect) {
      setOpen(false);
    }
    onChange(value);
  };

  const onCreateNewOption = useCallback(async () => {
    try {
      const newValue = await onAddNew?.(searchValue);
      if (!isNil(newValue)) {
        handleChange(Array.isArray(value)
          ? [...(value as TOption[]), newValue]
          : newValue);
      }
    } catch (error) {
      console.error(error);
    }
  }, [searchValue]);

  const showAddNew = useMemo(
    () => !isNil(onAddNew)
     && !isEmptyString(searchValue)
     && !optionSet.has(searchValue.toLowerCase().trim()),
    [searchValue, optionSet],
  );

  const filteredOptions = useMemo(
    () => (isNil(customFilterOptions)
      ? options as TOption[]
      : customFilterOptions?.(options as TOption[], searchValue)),
    [options, searchValue, customFilterOptions],
  );

  const allOptions = useMemo(
    () => [{}, ...(filteredOptions as TOption[])],
    [filteredOptions],
  );

  const [highlightedOption, setHighlightedOption] = useState<TOption | null>(null);

  useEffect(() => {
    if (!open) {
      setHighlightedOption(null);
    }
  }, [open]);

  return (
    <AutoCompletedInput<TOption>
      multiple={multiple}
      id={id}
      onOpen={() => { setOpen(true); }}
      onClose={() => { setOpen(false); }}
      open={open}
      disableRemove={disableRemove}
      value={value}
      isOptionEqualToValue={isOptionEqualToValue}
      clearOnBlur={clearOnBlur}
      blurOnSelect={blurOnSelect}
      disableCloseOnSelect={disableCloseOnSelect}
      disabled={readonly}
      options={allOptions as TOption[]}
      filterOptions={(x) => x}
      slotProps={{
        listbox: {
          component: Dropdown,
          pageProperties: isNil(pageProperties) ? null : pageProperties,
          fetchNextPage,
          isLoadingMore,
          id,
          count: allOptions?.length,
          searchValue,
          isSearching: isFetching,
          awaitingInitialSearch,
          emptyText,
          className: classes?.listbox ?? '',
          onAddNew: showAddNew ? onCreateNewOption : undefined,
          retainScrollOnSelection: multiple && disableCloseOnSelect,
          selectionCount: Array.isArray(value) ? value.length : 0,
          type,
        },
      } as Object}
      getLabel={getLabel}
      renderTags={renderTags}
      renderOption={(props: Record<string, any>, option: TOption) => (
        <DropdownOptionRow
          key={getUniqueId?.(option)}
          id={getUniqueId?.(option)}
          label={getLabel?.(option)}
          props={props}
        >
          {renderOptionRow?.(option)}
        </DropdownOptionRow>
      )}
      inputRef={inputRef}
      className="w-full"
      onChange={handleChange}
      onInputChange={handleInputChange}
      dropdownClassName={clsx('!overflow-hidden p-2', classes?.dropdownContainer)}
      popupIcon={isFetching || isLoading ? <Spinner /> : undefined}
      InputProps={{
        ...InputProps,
        autoFocus: InputProps?.autoFocus ?? true,
      }}
      onHighlightChange={(event, option) => {
        setHighlightedOption(option);
      }}
      onKeyDown={(event: KeyboardEvent) => {
        const highlightedOptionId = getUniqueId?.(highlightedOption as TOption) ?? '';
        if (event.key === 'Enter' && isEmptyString(highlightedOptionId)) {
          event.preventDefault();
          event.defaultMuiPrevented = true;
          onCreateNewOption();
          setOpen(false);
        }
      }}
      classes={omit(classes, ['dropdownContainer', 'listbox'])}
      {...props}
    />
  );
}
